import { createApi } from "@reduxjs/toolkit/query/react"
import { baseQuery } from "../baseQuerySl2"

/** A stored report object, as the run's manifest describes it. */
export type ReservationSyncObjectDto = {
  kind: "drift" | "sync" | "revert" | "snapshots"
  key: string
  bytes: number
  count: number
  rowFrom: number
  rowTo: number
  idFrom?: string
  idTo?: string
  sha256: string
}

/** The five values a source's read can take. `NOT_APPLICABLE` is not a read. */
export type ReadStateDto =
  | "COMPLETE"
  | "TRUNCATED"
  | "UNVERIFIED"
  | "FAILED"
  | "NOT_APPLICABLE"

export type SourceReadDto = {
  readState: ReadStateDto
  fetched: number | null
  totalReported: number | null
  exhaustedOnShortPage?: boolean
  caption?: string
  /** Read from an on-disk capture, not live (`--v2-snapshot`). Absent = read live. */
  fromSnapshot?: { fetchedAtIso: string; ageHours: number }
  error?: string
  /** NOT_APPLICABLE because the operator skipped it, not because it does not apply. */
  skippedByOperator?: boolean
}

export type SourceKeyDto = "v1" | "v2" | "ddb" | "olap"

/** May I act on this run's numbers? Three levels, never two (LOCKER-421 S1). */
export type VerdictDto = "ACTIONABLE" | "QUALIFIED" | "INCONCLUSIVE"

export type ReservationSyncDto = {
  id: string
  runId: string
  tenantId: string
  /** Context and a list grouping. A tenant is never itself assessed (OOS10). */
  tenantName?: string
  lockerId: string
  lockerName?: string
  sstLockerId: string
  /** v1 and v2 runs are read through different partner endpoints — never hide it. */
  sstApiVersion: "v1" | "v2"
  status: "DRIFT_ASSESSED" | "SYNC_APPLIED" | "SYNC_REVERTED" | "SYNC_FAILED"
  /** The WORST of the four source read states. `sources` is the real answer. */
  readState: "COMPLETE" | "TRUNCATED" | "UNVERIFIED" | "FAILED"
  /**
   * All four sources. Absent on a run stored before LOCKER-421 — the page shows the
   * single rolled-up chip for those rather than inventing four states.
   */
  sources?: Record<SourceKeyDto, SourceReadDto>
  /** Absent on a pre-LOCKER-421 run, which is shown unlabelled rather than guessed. */
  verdict?: VerdictDto
  snapshotShape?: { v1Shaped: number; v2Shaped: number }
  /**
   * Per counted-never-drift class. A VALUE of `null` means the source behind that
   * class was never read — rendered as an em dash, never as a 0.
   */
  nonDriftCounts?: Record<string, number | null> | null
  /** SHA-256 binding the plan the operator saw to the writes that landed. */
  planChecksum?: string
  confirmation?: {
    outcome:
      | "confirmed"
      | "declined"
      | "nonTty"
      | "emptyPlan"
      | "refusedUnpublishable"
    typed?: string
    confirmedAtIso?: string
  }
  operatorProfile: string
  fieldsCompared: string[]
  sstFetched: number
  sstTotalReported: number | null
  sl2Fetched: number
  /**
   * `null` on an INCONCLUSIVE run. Rendered as an em dash, NEVER as a zero — a
   * zero reads as "no drift found", which is the exact wrong conclusion to draw
   * from rows nobody read.
   */
  driftTotal: number | null
  counts: Record<string, number> | null
  syncCounts?: Record<string, number>
  revertCounts?: Record<string, number>
  objects: ReservationSyncObjectDto[]
  snapshotsIncludeCodes?: boolean
  startedAt: string
  finishedAt?: string
  /**
   * This locker's own assessment time. NOT `finishedAt - startedAt`: `startedAt`
   * is the whole sweep's start instant, shared by every run it published.
   */
  sweepDurationMs?: number
  syncAppliedAt?: string
  revertedAt?: string
  errors?: string[]
}

type GetThingsSaResponse<T> = {
  things: T[]
  meta: { count: number; nextToken?: string }
}

/**
 * Read-only client for the stored reservation-drift runs (LOCKER-374 D21).
 *
 * There is no mutation endpoint here and none on the server: the trail is
 * append-only, and objects age out on the bucket's lifecycle rule.
 */
export const reservationSyncApi = createApi({
  reducerPath: "reservationSyncApi",
  baseQuery,
  endpoints: (builder) => ({
    getReservationSyncs: builder.query<
      GetThingsSaResponse<ReservationSyncDto>,
      { lockerId?: string; limit?: number } | void
    >({
      query: (args) => ({
        url: "things",
        params: {
          "ap.name": "SaGetReservationSyncs",
          ...(args?.limit ? { "ap.limit": args.limit } : {}),
          // lodash `set` on the server turns `ap.lockerIds[0]` into an array.
          ...(args?.lockerId ? { "ap.lockerIds[0]": args.lockerId } : {}),
        },
      }),
    }),

    /**
     * A short-lived presigned URL for ONE stored object.
     *
     * `keepUnusedDataFor: 0` — the URL expires in five minutes and carries locker
     * access codes when the object is a snapshot part. Caching it would serve a
     * stale, still-live token to the next reader.
     */
    getReservationSyncObjectUrl: builder.query<
      {
        data: {
          url: string
          expiresInSeconds: number
          kind: string
          bytes: number
          count: number
        }
      },
      { runId: string; tenantId: string; key: string }
    >({
      query: ({ runId, tenantId, key }) => ({
        url: "reservation-syncs/object",
        params: { runId, tenantId, key },
      }),
      keepUnusedDataFor: 0,
    }),
  }),
})

export const {
  useGetReservationSyncsQuery,
  useLazyGetReservationSyncObjectUrlQuery,
} = reservationSyncApi
